import { useMemo, useState } from "react";
import {
  useWebsitePageMetrics,
  type WebsitePageMetrics,
} from "@/hooks/api/use-website-page-metrics";
import { useWebpageFilterState } from "@/hooks/use-webpage-filter-state";
import { useWebpageSelection } from "@/hooks/use-webpage-selection";
import type { MetricsFilters } from "@/lib/filters";
import { ChannelSelector } from "./channel-selector";
import { WebpageMetricCards } from "./metric-cards";
import { MetricDrilldown } from "./metric-drilldown";
import {
  DEFAULT_WEBPAGE_METRIC_ORDER,
  DRILLDOWN_LIMIT,
} from "./webpage-metrics-config";

interface WebpageScorecardProps {
  channelId?: string;
  onChannelChange?: (channelId: string) => void;
}

export function WebpageScorecard({
  channelId = "all",
  onChannelChange,
}: WebpageScorecardProps = {}) {
  const { selectedWebpageUrl } = useWebpageSelection();
  const [selectedMetric, setSelectedMetric] = useState<
    keyof WebsitePageMetrics | null
  >(null);

  // Get filter state for webpage explorer
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  } = useWebpageFilterState("webpages::explorer");

  // Build filters object for API calls
  const filters = useMemo((): MetricsFilters | undefined => {
    const filterObj: MetricsFilters = {};

    if (hasAccountFilters) {
      filterObj.account_filters = accountFilters;
    }

    if (hasOpportunityFilters) {
      filterObj.opportunity_filters = opportunityFilters;
    }

    if (hasTouchpointFilters) {
      filterObj.touchpoint_filters = touchpointFilters;
    }

    return Object.keys(filterObj).length > 0 ? filterObj : undefined;
  }, [
    hasAccountFilters,
    accountFilters,
    hasOpportunityFilters,
    opportunityFilters,
    hasTouchpointFilters,
    touchpointFilters,
  ]);

  // Get webpage metrics - fetch all metrics
  const {
    data: metrics,
    isLoading,
    loadingStates,
  } = useWebsitePageMetrics({
    url: selectedWebpageUrl || null,
    channelFilter: channelId !== "all" ? channelId : undefined,
    metrics: DEFAULT_WEBPAGE_METRIC_ORDER,
    filters,
    enabled: !!selectedWebpageUrl,
  });

  return (
    <div className="space-y-4">
      <WebpageMetricCards
        metrics={metrics}
        isLoading={isLoading}
        loadingStates={loadingStates}
        onMetricSelect={setSelectedMetric}
        leftControls={
          selectedWebpageUrl ? (
            <ChannelSelector
              selectedChannelId={channelId}
              onChannelChange={(id) => onChannelChange?.(id)}
              webpageUrl={selectedWebpageUrl}
              className="h-8 px-3 text-xs"
            />
          ) : null
        }
      />

      {selectedMetric && (
        <MetricDrilldown
          open={!!selectedMetric}
          onOpenChange={(open) => !open && setSelectedMetric(null)}
          webpageUrl={selectedWebpageUrl || null}
          metric={selectedMetric}
          channelId={channelId}
          metrics={metrics}
          filters={filters}
          drillDownLimit={DRILLDOWN_LIMIT}
        />
      )}
    </div>
  );
}
